/**
 * Centralized Event Configuration
 *
 * This file manages all time-based events in the app with dynamic year calculation.
 * Events are defined relative to the current year to avoid hard-coding dates.
 */

/**
 * Get the current year or a future year for event calculations
 * @param {number} offset - Years to add to current year (default: 0)
 * @returns {number} The calculated year
 */
function getEventYear(offset = 0) {
  return new Date().getFullYear() + offset;
}

/**
 * Event configuration object
 * Each event has functions that generate start/end dates dynamically
 */
export const EVENT_CONFIG = {
  valentines: {
    // Countdown period: Jan 18 - Feb 1
    countdownStart: () => {
      const year = getEventYear();
      return new Date(year, 0, 18); // January 18
    },
    countdownEnd: () => {
      const year = getEventYear();
      return new Date(year, 1, 1); // February 1
    },
    // Challenge period: Jan 23 - Feb 17
    challengeStart: () => {
      const year = getEventYear();
      return new Date(year, 0, 23); // January 23
    },
    challengeEnd: () => {
      const year = getEventYear();
      return new Date(year, 1, 17); // February 17
    },
    // Leaderboard display: Feb 1 - Feb 15
    leaderboardStart: () => {
      const year = getEventYear();
      return new Date(year, 1, 1); // February 1
    },
    leaderboardEnd: () => {
      const year = getEventYear();
      return new Date(year, 1, 15); // February 15
    },
    // Thank you display: Feb 15 onwards
    thankYouStart: () => {
      const year = getEventYear();
      return new Date(year, 1, 15); // February 15
    },
    // Countdown end date as string for component
    getCountdownString: () => {
      const year = getEventYear();
      return `February 1, ${year}`;
    }
  },

  ild: {
    // ILD (International Lovemaking Day) Premium Access: June 9-11
    premiumStart: () => {
      const year = getEventYear();
      return new Date(year, 5, 9); // June 9
    },
    premiumEnd: () => {
      const year = getEventYear();
      return new Date(year, 5, 11); // June 11
    },
    // Countdown period: May 24 - June 1
    countdownStart: () => {
      const year = getEventYear();
      return new Date(year, 4, 24); // May 24
    },
    countdownEnd: () => {
      const year = getEventYear();
      return new Date(year, 5, 1); // June 1
    },
    // Challenge period: May 24 - June 11
    challengeStart: () => {
      const year = getEventYear();
      return new Date(year, 4, 24); // May 24
    },
    challengeEnd: () => {
      const year = getEventYear();
      return new Date(year, 5, 11); // June 11
    },
    // Leaderboard display: June 1 - June 11
    leaderboardStart: () => {
      const year = getEventYear();
      return new Date(year, 5, 1); // June 1
    },
    leaderboardEnd: () => {
      const year = getEventYear();
      return new Date(year, 5, 11); // June 11
    },
    // Thank you display: June 11 onwards
    thankYouStart: () => {
      const year = getEventYear();
      return new Date(year, 5, 11); // June 11
    },
    // Countdown end date as string for component
    getCountdownString: () => {
      const year = getEventYear();
      return `June 1, ${year}`;
    }
  },

  sextember: {
    // Pre-event countdown: Aug 23 - Sep 1
    preEventCountdownStart: () => {
      const year = getEventYear();
      return new Date(year, 7, 23); // August 23
    },
    preEventCountdownEnd: () => {
      const year = getEventYear();
      return new Date(year, 8, 1); // September 1
    },
    // Challenge period: Aug 23 - Oct 3
    challengeStart: () => {
      const year = getEventYear();
      return new Date(year, 7, 23); // August 23
    },
    challengeEnd: () => {
      const year = getEventYear();
      return new Date(year, 9, 3); // October 3
    },
    // Active challenge: Sep 1 - Oct 1
    activeStart: () => {
      const year = getEventYear();
      return new Date(year, 8, 1); // September 1
    },
    activeEnd: () => {
      const year = getEventYear();
      return new Date(year, 9, 1); // October 1
    },
    // Thank you display: Oct 1 onwards
    thankYouStart: () => {
      const year = getEventYear();
      return new Date(year, 9, 1); // October 1
    },
    // Countdown end date as string for component
    getCountdownString: () => {
      const year = getEventYear();
      return `September 1, ${year}`;
    },
    // Date range for day calculation (Aug 15 - Sep 30)
    dayCalcStart: () => {
      const year = getEventYear();
      return new Date(year, 7, 15); // August 15
    },
    dayCalcEnd: () => {
      const year = getEventYear();
      return new Date(year, 8, 30); // September 30
    }
  }
};

/**
 * Check if today is within a date range
 * @param {Date} startDate - Start date of range
 * @param {Date} endDate - End date of range
 * @returns {boolean} True if today is within range
 */
export function isInDateRange(startDate, endDate) {
  const today = new Date();
  return startDate <= today && today < endDate;
}

/**
 * Check if today is after a specific date
 * @param {Date} date - Date to compare against
 * @returns {boolean} True if today is after the date
 */
export function isAfterDate(date) {
  const today = new Date();
  return today >= date;
}

/**
 * Helper to check if any challenge is currently active
 * Useful for showing challenge banners on home screen
 */
export function getActiveChallenge() {
  const valentines = EVENT_CONFIG.valentines;
  const ild = EVENT_CONFIG.ild;
  const sextember = EVENT_CONFIG.sextember;

  if (isInDateRange(valentines.challengeStart(), valentines.challengeEnd())) {
    return 'valentines';
  }

  if (isInDateRange(ild.challengeStart(), ild.challengeEnd())) {
    return 'ild';
  }

  if (isInDateRange(sextember.challengeStart(), sextember.challengeEnd())) {
    return 'sextember';
  }

  return null;
}

export default EVENT_CONFIG;
